import { FC, useEffect, useState } from "react";
import {
  FILE_URL,
  Investigation,
  PreliminaryInquiry,
  Roles,
  Witness,
} from "types";
import { constructFileUrl, DateView, hasRole } from "utils/helper";
import PreliminaryForm from "./PreliminaryForm";
import PreliminaryDetail from "./PreliminaryDetail";
import { useAuth } from "context/AuthContext";
import { fetchPreliminaryByWitness } from "services/prosecutorPoliceService";

type Props = {
  witness: Witness | undefined;
  investigation: Investigation;
  allPreliminaries: PreliminaryInquiry[] | [];
  isOpen: boolean;
  onClose: (refetch: boolean) => void;
};

const WitnessDetail: FC<Props> = ({
  witness,
  investigation,
  allPreliminaries,
  isOpen,
  onClose,
}) => {
  const { loginData } = useAuth();
  const [isPreliminaryOpen, setIsPreliminaryOpen] = useState(false);
  const isClerk = hasRole(loginData?.roles, [
    Roles.clerk,
    Roles.variousClerk,
    Roles.economicClerk,
    Roles.corruptionClerk,
  ]);
  const [preliminary, setPreliminary] = useState<PreliminaryInquiry>();

  const fetchPreliminaryData = () => {
    fetchPreliminaryByWitness(witness?.uu_id?.toString() || "").then(
      (response) => {
        if (response && response[0]) {
          setPreliminary(response[0]);
          setIsPreliminaryOpen(true);
        }
      }
    );
  };

  useEffect(() => {
    if (witness) {
      fetchPreliminaryData();
    }
  }, [witness]);

  useEffect(() => {
    setIsPreliminaryOpen(false);
  }, [isOpen]);

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div
        className={`relative w-full bg-white p-8 shadow-lg rounded-lg overflow-y-auto flex flex-row ${
          isPreliminaryOpen ? "max-w-6xl gap-5" : "max-w-2xl"
        }`}
      >
        <div
          className={`overflow-y-scroll overflow-x-hidden pr-5 ${
            !isPreliminaryOpen ? "w-full" : "min-w-2xl border-r-2 pr-6"
          } mb-4`}
        >
          <div className="text-base font-semibold text-white bg-[#212121] p-3 inline-block rounded-md">
            Witness ID &nbsp;&nbsp; : &nbsp;&nbsp;
            {witness?.identity_card_number}
          </div>

          <br />

          <div className="flex flex-row gap-4 mt-3 items-start">
            <div className="bg-[#BCBCBC66] rounded-md">
              <img
                src={(() => {
                  const doc = witness?.documents?.find(
                    (doc) => doc.document_type === "witness_picture"
                  );
                  return doc
                    ? constructFileUrl(doc, FILE_URL.WITNESS)
                    : require("../../../../images/imageVector.png");
                })()}
                alt="User"
                className="w-auto h-[110px] rounded"
              />
            </div>
            <div className="flex flex-col w-[250px]">
              <div className="bg-[#9B0505] w-fit px-2 py-[2px] text-white rounded-md text-center font-medium text-sm">
                {typeof witness?.investigation === "object" &&
                  witness?.investigation?.CR_number}
              </div>
              <h2 className="text-[22px] font-semibold leading-7">
                {witness?.first_name} {witness?.middle_name}{" "}
                {witness?.last_name}
              </h2>
              <p className="text-base font-normal text-[#8B8B8B]">
                {witness?.phone_number}
              </p>
              <p className="text-base font-normal text-[#8B8B8B]">
                {witness?.email}
              </p>
            </div>
          </div>

          {[
            { property: "Gender", value: witness?.gender },
            { property: "Age", value: witness?.age },
            {
              property: "Educational Level",
              value: witness?.education_level,
            },
            { property: "Job position", value: witness?.job_position },
            { property: "Ethnicity", value: witness?.ethnicity },
            { property: "Nationality", value: witness?.nationality },
            { property: "Religion", value: witness?.religion },
            {
              property: "Date of Birth",
              value: DateView(witness?.date_of_birth),
            },
            { property: "Phone Number", value: witness?.phone_number },
            { property: "Email", value: witness?.email },
            {
              property: "Identity Card Number",
              value: witness?.identity_card_number,
            },
            { property: "Testimony", value: witness?.testimony },
            { property: "Statement", value: witness?.statement },
          ]?.map((info, index) => (
            <div
              key={index}
              className="flex flex-row gap-4 mt-3 items-start justify-between border-b border-b-[#E9E9E9] pb-3"
            >
              <p className="font-normal text-base text-[#0A1D39]">
                {info?.property}
              </p>
              <p className="font-normal text-base text-[#0A1D39]">
                {typeof info?.value === "boolean" ||
                typeof info?.value === "object"
                  ? info?.value ?? "-"
                  : info?.value}
              </p>
            </div>
          ))}

          <div className="bg-[#F8F8F8] p-3 rounded-xl mt-5">
            <p className="text-base font-semibold">Address Location</p>
            <hr className="text-[#8B8B8B] my-3 w-[50%]" />

            <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
              {[
                { property: "Region", value: witness?.region },
                { property: "Zone", value: witness?.zone },
                { property: "Woreda", value: witness?.woreda },
                { property: "Kebele", value: witness?.kebele },
                { property: "House_number", value: witness?.house_number },
                { property: "City", value: witness?.city },
              ]?.map((info, index) => (
                <div key={index}>
                  <p className="font-normal text-sm text-[#747171]">
                    {info.property}
                  </p>
                  <p className="font-semibold text-base text-[#0A1D39]">
                    {info.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {investigation && isClerk && (
            <input
              type="button"
              className="border border-[#212121] bg-[#212121] w-full my-3 py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
              value={"Preliminary Req"}
              onClick={() => setIsPreliminaryOpen(!isPreliminaryOpen)}
            />
          )}
        </div>
        {isPreliminaryOpen && investigation && witness && (
          <div className="w-full min-w-2xl">
            {preliminary ? (
              <PreliminaryDetail
                preliminary={preliminary}
                onClose={() => onClose(false)}
              />
            ) : (
              <PreliminaryForm
                investigation={investigation}
                action={"Add"}
                allPreliminaries={allPreliminaries}
                onClose={() => onClose(true)}
                witness={witness}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WitnessDetail;
